import { camelCase, get, keyBy, sortBy, startCase } from 'lodash';
import {
  FieldTypeDisplay,
  FieldSection,
  FieldSectionSubType,
  FieldSectionType,
} from 'api/field-sections/types';
import { FieldPayload } from 'api/fields';
import { RbacPermissionRule } from 'api/rbac/rbac-types';
import { Field, ProjectStatusGroup, TaskStatusGroup } from 'api/tasks';
import { CompanyFields } from 'components/organisms/customer-portfolio/detail/overview/fields/fields-category';
import { Translate, TranslationKey } from 'hooks/use-translation';
import { unreachable } from 'utils/unreachable';
import { getArbitraryColor } from './options/color-dropdown';
import {
  FieldDataType,
  FieldOption,
  FieldSectionOption,
  FieldType,
  FieldTypeOption,
  FieldTypes,
  Values,
} from './types';

const SELECT_TYPES = [
  FieldTypes.SingleSelect,
  FieldTypes.MultiSelect,
  FieldTypes.Level,
];

export const isSelectField = (option: FieldTypeOption) => {
  return option ? SELECT_TYPES.includes(option.value) : false;
};
export const isNoteField = (option: FieldTypeOption) =>
  option ? option.value === FieldTypes.Note : false;

export const EMPTY_FIELD_OPTION: FieldOption[] = [
  {
    label: '',
    color: getArbitraryColor(0),
    deletable: true,
    editable: true,
  },
];

export const isRatingFieldTypeOption = (option: FieldTypeOption) =>
  option ? option.value === FieldTypes.Rating : false;

export const toFieldType = (type: FieldTypes): FieldType => {
  switch (type) {
    case FieldTypes.Text:
    case FieldTypes.MultiLineText:
    case FieldTypes.Boolean:
    case FieldTypes.Date:
    case FieldTypes.Number:
    case FieldTypes.SingleSelect:
    case FieldTypes.SingleUserSelect:
    case FieldTypes.Level:
      return FieldType.SingleSelect;
    case FieldTypes.MultiSelect:
    case FieldTypes.MultiUserSelect:
      return FieldType.MultiSelect;
    case FieldTypes.Rating:
      return FieldType.Rating;
    case FieldTypes.Note:
      return FieldType.MultiSelect;
    default:
      return unreachable(type);
  }
};

export const toFieldDataType = (type: FieldTypes): FieldDataType => {
  switch (type) {
    case FieldTypes.Text:
    case FieldTypes.SingleSelect:
    case FieldTypes.MultiSelect:
    case FieldTypes.Level:
      return FieldDataType.Text;
    case FieldTypes.MultiLineText:
      return FieldDataType.MultiLineText;
    case FieldTypes.Number:
      return FieldDataType.Number;
    case FieldTypes.Boolean:
      return FieldDataType.Boolean;
    case FieldTypes.Date:
      return FieldDataType.Date;
    case FieldTypes.SingleUserSelect:
    case FieldTypes.MultiUserSelect:
      return FieldDataType.User;
    case FieldTypes.Rating:
      return FieldDataType.Number;
    case FieldTypes.Note:
      return FieldDataType.Note;
    default:
      return unreachable(type);
  }
};

export const toFieldTypeDisplay = (
  type: FieldTypes,
): FieldTypeDisplay | undefined => {
  switch (type) {
    case FieldTypes.Level:
      return FieldTypeDisplay.Level;
    default:
      return undefined;
  }
};

export const toType = (
  fieldDataType: FieldDataType,
  optionsAvailable: boolean,
  isMulti: boolean,
): FieldTypes => {
  switch (fieldDataType) {
    case FieldDataType.Boolean:
      return FieldTypes.Boolean;
    case FieldDataType.MultiLineText:
      return FieldTypes.MultiLineText;
    case FieldDataType.Text:
      return optionsAvailable
        ? isMulti
          ? FieldTypes.MultiSelect
          : FieldTypes.SingleSelect
        : FieldTypes.Text;
    case FieldDataType.Number:
      return FieldTypes.Number;
    case FieldDataType.Date:
      return FieldTypes.Date;
    case FieldDataType.Note:
      return FieldTypes.Note;
    case FieldDataType.User:
      return isMulti ? FieldTypes.MultiUserSelect : FieldTypes.SingleUserSelect;
    default:
      return unreachable(fieldDataType);
  }
};

export const getType = (field: Field): FieldTypes => {
  const { optionsAvailable, fieldDataType, fieldType, fieldTypeDisplay } =
    field;

  if (fieldTypeDisplay === FieldTypeDisplay.Level) {
    return FieldTypes.Level;
  }

  if (field.fieldType === FieldTypes.Rating) {
    return FieldTypes.Rating;
  }

  return toType(
    fieldDataType as FieldDataType,
    optionsAvailable,
    fieldType === FieldType.MultiSelect,
  );
};

export const valuesToPayload = (values: Values): FieldPayload => {
  const { type, metaData, fieldSection, ...rest } = values;

  return {
    ...rest,
    metaData: metaData
      ? {
          ...metaData,
          tags:
            type.value === FieldTypes.Note
              ? metaData.tags
                ? [...metaData?.tags].map((option, index) => ({
                    ...option,
                    group: option.group?.value,
                    position: index + 1,
                  }))
                : []
              : undefined,
          ratingScale: metaData.ratingScale?.value,
          options: metaData.options
            ? [...metaData?.options].map((option, index) => ({
                ...option,
                group: option.group?.value,
                position: index + 1,
              }))
            : [],
        }
      : metaData,
    fieldType: toFieldType(type.value),
    fieldDataType: toFieldDataType(type.value),
    fieldSectionId: fieldSection ? fieldSection.value : undefined,
  };
};

export const taskStatusGroupOptions = Object.keys(TaskStatusGroup)
  .filter((x) => !(parseInt(x, 10) >= 0))
  .map((group) => {
    return {
      label: startCase(group),
      value: TaskStatusGroup[group] as number,
      group: TaskStatusGroup[group],
    };
  });

export const projectStatusGroupOptions = Object.keys(ProjectStatusGroup)
  .filter((x) => !(parseInt(x, 10) >= 0))
  .map((group) => {
    return {
      label: startCase(group),
      value: ProjectStatusGroup[group] as number,
      group: ProjectStatusGroup[group],
    };
  });

export const taskStatusGroupOptionsLookup = keyBy(
  taskStatusGroupOptions,
  'value',
);

export const projectStatusGroupOptionsLookup = keyBy(
  projectStatusGroupOptions,
  'value',
);

export const toValues = (
  field: Field,
  fieldTypeOptions: FieldTypeOption[] = [],
  fieldSections: FieldSection[] | undefined,
): Values => {
  const {
    fieldLabel = '',
    description = '',
    objectType,
    metaData,
    fieldSectionId,
  } = field;
  const type = getType(field);
  const meta = metaData
    ? {
        ...metaData,
        ratingScale: metaData.ratingScale
          ? {
              label: metaData.ratingScale?.toString(),
              value: metaData.ratingScale,
            }
          : undefined,
        tags: sortBy(
          (metaData.tags ?? [])
            .map((option) => ({ ...option, group: undefined }))
            .filter((option) => get(option, ['active'])),
          'position',
        ),
        options: sortBy(
          (metaData.options ?? [])
            .map((option) => {
              return {
                ...option,
                group: option.group
                  ? objectType === FieldSectionType.Task
                    ? taskStatusGroupOptionsLookup[option.group]
                    : projectStatusGroupOptionsLookup[option.group]
                  : undefined,
              };
            })
            .filter((option) => get(option, ['active'])),
          ['position'],
        ),
      }
    : metaData;

  const selectedFieldSection = fieldSections
    ? fieldSections.find((s) => s.fieldSectionId === fieldSectionId)
    : undefined;

  return {
    fieldLabel,
    private: field.private,
    description,
    objectType,
    metaData: meta,
    type: fieldTypeOptions.find((o) => o.value === type)!,
    fieldSection: selectedFieldSection
      ? {
          label: selectedFieldSection.fieldSectionName,
          value: selectedFieldSection.fieldSectionId,
          sectionType: selectedFieldSection.fieldSectionType,
          private: selectedFieldSection.private ?? false,
        }
      : null,
  };
};

export const getFieldTypeOptions = (
  translate: Translate,
  objectType?: FieldSectionType,
): FieldTypeOption[] =>
  Object.values(FieldTypes)
    .filter((e) => {
      if (e === FieldTypes.Rating) {
        return objectType === FieldSectionType.Project;
      }
      if (e === FieldTypes.Note) {
        return objectType === FieldSectionType.Company;
      }

      if (e === FieldTypes.Level) {
        return false;
      }

      return true;
    })
    .map((type) => ({
      label: translate(`settings.fields.type.${type}` as TranslationKey),
      value: type,
    }));

export const getFieldSections = (
  fieldSections: FieldSection[] | undefined,
  objectType: FieldSectionType,
): FieldSectionOption[] => {
  const updatableSections = fieldSections?.filter((fs) => !fs.fixed);

  if (updatableSections && updatableSections.length) {
    const updatedFieldSections = updatableSections.map((section) => {
      return {
        label: section.fieldSectionName,
        value: section.fieldSectionId,
        sectionType: objectType,
        private: section.private ?? false,
      };
    });

    if (objectType === FieldSectionType.Task) {
      return updatedFieldSections.slice(0);
    }

    return updatedFieldSections;
  }

  return [];
};

export const getInitialValues = (
  translate: Translate,
  objectType: Field['objectType'],
  field: Field | undefined,
  fieldSections: FieldSection[] | undefined,
  objectSubType?: Field['objectSubType'],
): Values => {
  const fieldTypeOptions = getFieldTypeOptions(translate, objectType);
  const defaultFieldType = fieldTypeOptions.find(
    (o) => o.value === FieldTypes.Text,
  )!;

  const updatedFieldSections = getFieldSections(fieldSections, objectType);

  return field
    ? toValues(field, fieldTypeOptions, fieldSections)
    : {
        isEnabled: true,
        fieldLabel: '',
        type: defaultFieldType,
        description: '',
        private: [
          FieldSectionType.Project,
          FieldSectionType.Users,
          FieldSectionType.Company,
          FieldSectionType.Sprint,
        ].includes(objectType),
        metaData: {
          options: [],
          tags: [],
          ratingScale: { label: translate('rating_fields.scale.5'), value: 5 },
        },
        objectType,
        objectSubType: objectSubType ?? FieldSectionSubType.Default,
        fieldSection:
          updatedFieldSections && updatedFieldSections.length
            ? {
                label: updatedFieldSections[0].label,
                value: updatedFieldSections[0].value,
                sectionType: objectType,
                private: updatedFieldSections[0].private ?? false,
              }
            : null,
      };
};

export const taskField = (field: Field): boolean =>
  field.objectType === FieldSectionType.Task;
export const projectField = (field: Field): boolean =>
  field.objectType === FieldSectionType.Project;
export const sprintField = (field: Field): boolean =>
  field.objectType === FieldSectionType.Sprint;

const default_task_fields = [
  'taskId',
  'atRisk',
  'taskName',
  'taskDescription',
  'privateTaskDescription',
  'dueDate',
  'startDate',
  'completedAt',
  'type',
  'isCsatEnabled',
];

export const isNativeField = ({
  customField,
  fieldName,
}: Pick<Field, 'customField' | 'fieldName'>): boolean =>
  default_task_fields.includes(camelCase(fieldName)) || !customField;

export const customField = (field: Field): boolean => {
  return !isNativeField(field);
};

const SPECIAL_PROJECT_FIELDS = [
  'startDate',
  'dueDate',
  'status',
  'annualizedRecurringRevenue',
  'projectFee',
];
const SPECIAL_PROJECT_FIELDS_NOT_TOGGLEABLE = [
  'startDate',
  'dueDate',
  'status',
];

export const isSpecialProjectField = (fieldName = ''): boolean => {
  return SPECIAL_PROJECT_FIELDS.includes(camelCase(fieldName));
};
export const isToggleableProjectField = (fieldName = ''): boolean => {
  return SPECIAL_PROJECT_FIELDS_NOT_TOGGLEABLE.includes(camelCase(fieldName));
};

const SPECIAL_TASK_FIELDS = [
  'priority',
  'effort',
  'progress',
  'taskDescription',
  'pointsCustom',
  'pointsExponential',
  'pointsLinear',
  'pointsFibonacci',
];

const ReadonlyCompanyFields = [
  CompanyFields.accountOwner,
  CompanyFields.description,
];

export const isTaskStatusField = ({
  fieldName,
  objectType,
}: Pick<Field, 'fieldName' | 'objectType'>): boolean => {
  return (
    objectType === FieldSectionType.Task && camelCase(fieldName) === 'status'
  );
};

export const isProjectStatusField = ({
  fieldName,
  objectType,
}: Pick<Field, 'fieldName' | 'objectType'>): boolean => {
  return (
    objectType === FieldSectionType.Project && camelCase(fieldName) === 'status'
  );
};

export const isRatingField = (
  field: Pick<Field, 'fieldType'> | undefined,
): boolean => {
  return field !== null && field !== undefined
    ? field.fieldType === FieldTypes.Rating
    : false;
};

const isSpecialTaskField = (fieldName = ''): boolean => {
  return SPECIAL_TASK_FIELDS.includes(camelCase(fieldName));
};

export const canShowInProjectFieldsSettings = (field: Field): boolean => {
  return isSpecialProjectField(field.fieldName) || field.customField;
};

export const canShowInTaskFieldsSettings = (field: Field): boolean => {
  return isSpecialTaskField(field.fieldName) || customField(field);
};

export const canShowInTeamMemberFieldsSettings = (field: Field): boolean => {
  return customField(field);
};

export const canDisableField = (field: Field): boolean => {
  const { objectType, fieldName } = field;

  switch (objectType) {
    case FieldSectionType.Project:
      return true;
    case FieldSectionType.Task:
      return !isTaskStatusField(field) && !isSpecialTaskField(fieldName);
    case FieldSectionType.ProjectPhase:
      return false;
    case FieldSectionType.Users:
      return true;
    case FieldSectionType.Financial:
      return false;
    case FieldSectionType.Sprint:
      return true;
    case FieldSectionType.Company:
      return !ReadonlyCompanyFields.includes(fieldName);
    case FieldSectionType.Timeoff:
      return false;
    default:
      return unreachable(objectType);
  }
};

export const canEditField = (field: Field): boolean => {
  const { objectType, fieldName } = field;

  switch (objectType) {
    case FieldSectionType.Project:
      return isProjectStatusField(field) || !isSpecialProjectField(fieldName);
    case FieldSectionType.Task:
      return isTaskStatusField(field) || !isSpecialTaskField(fieldName);
    case FieldSectionType.ProjectPhase:
      return false;
    case FieldSectionType.Users:
      return true;
    case FieldSectionType.Financial:
      return false;
    case FieldSectionType.Sprint:
    case FieldSectionType.Company:
      return true;
    case FieldSectionType.Timeoff:
      return false;
    default:
      return unreachable(objectType);
  }
};

export const canMoveField = (field: Field): boolean => {
  const { objectType, fieldName } = field;

  switch (objectType) {
    case FieldSectionType.Project:
      return true;
    case FieldSectionType.Task:
      return (
        !isNativeField(field) &&
        !isTaskStatusField(field) &&
        !isSpecialTaskField(fieldName)
      );
    case FieldSectionType.ProjectPhase:
      return false;
    case FieldSectionType.Users:
      return true;
    case FieldSectionType.Financial:
      return false;
    case FieldSectionType.Sprint:
    case FieldSectionType.Company:
      return true;
    case FieldSectionType.Timeoff:
      return false;
    default:
      return unreachable(objectType);
  }
};

export const canDeleteField = (field: Field): boolean => {
  const { objectType, fieldName } = field;

  switch (objectType) {
    case FieldSectionType.Project:
      return !isSpecialProjectField(fieldName);
    case FieldSectionType.Task:
      return !isTaskStatusField(field) && !isSpecialTaskField(fieldName);
    case FieldSectionType.ProjectPhase:
      return false;
    case FieldSectionType.Users:
      return true;
    case FieldSectionType.Financial:
      return false;
    case FieldSectionType.Sprint:
      return true;
    case FieldSectionType.Company:
      return !ReadonlyCompanyFields.includes(fieldName);
    case FieldSectionType.Timeoff:
      return false;
    default:
      return unreachable(objectType);
  }
};

export const getFieldNamePlaceholderKey = (type: FieldTypes): TranslationKey =>
  `settings.fields.field.name.placeholder.${type}` as TranslationKey;

export const getEditRuleForField = (
  field: Field,
): RbacPermissionRule | undefined => {
  switch (field.objectType) {
    case FieldSectionType.Project:
      return 'SETTINGS_PROJECT_FIELD:WRITE';
    case FieldSectionType.Task:
      return 'SETTINGS_TASK_FIELD:WRITE';
    case FieldSectionType.Sprint:
      return 'SPRINT_SETTINGS:WRITE';
    case FieldSectionType.Users:
      if (field.objectSubType === FieldSectionSubType.Skill) {
        return 'SETTINGS_SKILL_FIELD:WRITE';
      }
      if (field.objectSubType === FieldSectionSubType.ExternalUser) {
        return 'SETTINGS_CUSTOMER_TEAM_MEMBER_FIELD:WRITE';
      }

      return 'SETTINGS_TEAM_MEMBER_FIELD:WRITE';
    case FieldSectionType.Company:
      return 'SETTINGS_CUSTOMER_COMPANY_FIELD:WRITE';
    default:
      return undefined;
  }
};
export const getDeleteRuleForField = (
  field: Field,
): RbacPermissionRule | undefined => {
  switch (field.objectType) {
    case FieldSectionType.Project:
      // return 'PROJECT_FIELD:DELETE';
      return 'SETTINGS_PROJECT_FIELD:DELETE';
    case FieldSectionType.Task:
      // return 'TASK_FIELD:DELETE';
      return 'SETTINGS_TASK_FIELD:DELETE';
    case FieldSectionType.Sprint:
      return 'SPRINT_SETTINGS:WRITE';
    case FieldSectionType.Users:
      if (field.objectSubType === FieldSectionSubType.Skill) {
        return 'SETTINGS_SKILL_FIELD:DELETE';
      }
      if (field.objectSubType === FieldSectionSubType.ExternalUser) {
        return 'SETTINGS_CUSTOMER_TEAM_MEMBER_FIELD:DELETE';
      }

      // return 'TEAM_MEMBER_FIELD:DELETE';
      return 'SETTINGS_TEAM_MEMBER_FIELD:DELETE';
    case FieldSectionType.Company:
      return 'SETTINGS_CUSTOMER_COMPANY_FIELD:DELETE';
    default:
      return undefined;
  }
};
